import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { color, severityTones } from '@cwos/ui-shared';
import { GeoMap } from './GeoMap.js';
import { loadGoogleMaps } from './googleMapsLoader.js';
import { MapUnavailableNotice } from './MapUnavailableNotice.js';
import { buildFenceSpecs, buildMarkerSpecs, buildRoutePath, escapeHtml, } from './googleMapsLogic.js';
import styles from './map.module.css';
const DEFAULT_HEIGHT = 460;
const ROUTE_STROKE = color.navy;
/** Marker kind → status family, mirroring the SVG renderer's per-kind colours. */
const KIND_TONES = {
    start: 'good',
    end: 'look',
    stop: 'unsure',
    event: 'look',
};
function markerColor(spec) {
    if (spec.kind === 'cluster')
        return color.navy;
    if (spec.kind === 'site')
        return color.navy;
    if (spec.kind === 'worker')
        return severityTones(spec.tone ?? 'sky').base;
    return severityTones(KIND_TONES[spec.kind]).base;
}
function makeMarker(map, spec, onSelect) {
    if (spec.kind === 'cluster') {
        return new google.maps.Marker({
            position: spec.position,
            map,
            title: `${spec.count ?? 0} workers here`,
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 11,
                fillColor: color.navy,
                fillOpacity: 1,
                strokeColor: color.surface,
                strokeWeight: 2,
            },
            label: {
                text: String(spec.count ?? 0),
                color: color.waterText,
                fontSize: '11px',
                fontWeight: '700',
            },
            clickable: false,
            zIndex: 5,
        });
    }
    const marker = new google.maps.Marker({
        position: spec.position,
        map,
        title: spec.label ?? spec.id,
        icon: {
            // A Site is a square; everything else is a circle. Same rule as the SVG
            // renderer, so the two layers never disagree about what a shape means.
            path: spec.kind === 'site' ? 'M -6 -6 L 6 -6 L 6 6 L -6 6 Z' : google.maps.SymbolPath.CIRCLE,
            scale: spec.kind === 'site' ? 1 : spec.selected ? 9 : 7,
            fillColor: markerColor(spec),
            fillOpacity: 1,
            strokeColor: spec.selected ? color.navy : color.surface,
            strokeWeight: spec.selected ? 3 : 2,
        },
        clickable: spec.clickable,
        zIndex: spec.kind === 'worker' ? 3 : 2,
    });
    if (spec.clickable) {
        marker.addListener('click', () => onSelect?.(spec.id));
    }
    return marker;
}
function openInfoWindow(map, spec, geocoder, infoWindow) {
    if (!infoWindow)
        return;
    // Privacy boundary (#5084): reverse-geocode ONLY a worker's own STAFF
    // coordinate, and only for an already-authorized viewer (this whole map is
    // gated by `location_history:view`). A worker marker carries no client data;
    // there is no client coordinate anywhere in this data model, so no
    // client-linked home/service coordinate can ever reach Google. This guard
    // makes that invariant explicit so a future caller cannot accidentally
    // geocode a punch/stop/route marker.
    if (spec.kind !== 'worker')
        return;
    const name = escapeHtml(spec.label ?? spec.id);
    infoWindow.setContent(`<div style="font: 13px Inter, sans-serif; line-height: 1.4"><strong>${name}</strong>` +
        `<div>Locating address…</div></div>`);
    infoWindow.setPosition(spec.position);
    infoWindow.open(map);
    if (!geocoder)
        return;
    geocoder.geocode({ location: spec.position }, (results, status) => {
        const address = status === google.maps.GeocoderStatus.OK && results && results.length > 0
            ? results[0]?.formatted_address
            : undefined;
        infoWindow.setContent(`<div style="font: 13px Inter, sans-serif; line-height: 1.4"><strong>${name}</strong>` +
            `<div>${address ? escapeHtml(address) : 'Address unavailable'}</div></div>`);
    });
}
export function GoogleMap({ mapsApiKey, ...geoProps }) {
    const { fences = [], markers = [], routes = [], height = DEFAULT_HEIGHT, selectedMarkerId, onMarkerSelect, } = geoProps;
    const hasContent = fences.length + markers.length + routes.length > 0;
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const overlaysRef = useRef([]);
    const infoWindowRef = useRef(null);
    const geocoderRef = useRef(null);
    const [mapReady, setMapReady] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    // `onMarkerSelect` is recreated every parent render; keep the latest in a ref
    // so the overlay redraw effect doesn't need it as a (churning) dependency.
    const onMarkerSelectRef = useRef(onMarkerSelect);
    onMarkerSelectRef.current = onMarkerSelect;
    useEffect(() => {
        if (!hasContent)
            return;
        let cancelled = false;
        loadGoogleMaps(mapsApiKey)
            .then(() => {
            if (cancelled || !containerRef.current)
                return;
            const map = new google.maps.Map(containerRef.current, {
                center: { lat: 0, lng: 0 },
                zoom: 11,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
                gestureHandling: 'greedy',
            });
            mapRef.current = map;
            infoWindowRef.current = new google.maps.InfoWindow();
            geocoderRef.current = new google.maps.Geocoder();
            setMapReady(true);
        })
            .catch(() => {
            if (!cancelled)
                setLoadFailed(true);
        });
        return () => {
            cancelled = true;
        };
    }, [mapsApiKey, hasContent]);
    // (Re)draw every overlay whenever the content or selection changes.
    useEffect(() => {
        const map = mapRef.current;
        if (!map)
            return;
        for (const overlay of overlaysRef.current)
            overlay.setMap(null);
        overlaysRef.current = [];
        infoWindowRef.current?.close();
        const specs = buildMarkerSpecs(markers, selectedMarkerId, onMarkerSelectRef.current);
        const fenceSpecs = buildFenceSpecs(fences);
        const bounds = new google.maps.LatLngBounds();
        let hasPoints = false;
        for (const fence of fenceSpecs) {
            overlaysRef.current.push(new google.maps.Circle({
                center: fence.center,
                radius: fence.radiusMeters,
                fillColor: color.sky,
                fillOpacity: 0.18,
                strokeColor: color.skyDeep,
                strokeWeight: 1,
                map,
            }));
            bounds.extend(fence.center);
            hasPoints = true;
        }
        for (const route of routes) {
            const path = buildRoutePath(route.points);
            if (!path)
                continue; // <2 breadcrumbs → punch markers only, no invented line
            overlaysRef.current.push(new google.maps.Polyline({
                path,
                strokeColor: ROUTE_STROKE,
                strokeOpacity: 0.85,
                strokeWeight: 3,
                clickable: false,
                map,
            }));
            for (const point of path) {
                bounds.extend(point);
                hasPoints = true;
            }
        }
        for (const spec of specs) {
            overlaysRef.current.push(makeMarker(map, spec, onMarkerSelectRef.current));
            bounds.extend(spec.position);
            hasPoints = true;
        }
        if (hasPoints)
            map.fitBounds(bounds, 48);
        const selectedWorker = specs.find((s) => s.kind === 'worker' && s.selected);
        if (selectedWorker) {
            openInfoWindow(map, selectedWorker, geocoderRef.current, infoWindowRef.current);
        }
    }, [mapReady, fences, markers, routes, selectedMarkerId]);
    // A load failure (blocked referrer, network error, ad blocker) is a
    // genuinely different state from "nothing to draw yet" — it gets a clear
    // label so a supervisor knows the interactive layer is down, not that the
    // day has no data. Empty content never attempted a load (the effect above
    // bails out first), so it stays silent and lets GeoMap's own empty state
    // speak for itself.
    if (loadFailed) {
        return (_jsxs("div", { children: [_jsx(MapUnavailableNotice, { reason: "load-failed" }), _jsx(GeoMap, { ...geoProps })] }));
    }
    if (!hasContent) {
        return _jsx(GeoMap, { ...geoProps });
    }
    return (_jsx("div", { className: styles.map, children: _jsx("div", { ref: containerRef, className: styles.gmap, style: { height }, role: "region", "aria-label": "Workforce location map (interactive)" }) }));
}
//# sourceMappingURL=GoogleMap.js.map